import { apiService } from './api';

/** Mirrors the backend PromotionDiscountType enum (serialised as an int). */
export enum PromotionDiscountType {
  Percentage = 0,
  FlatAmount = 1,
}

/** Mirrors the backend PromotionScope enum — which invoice lines the rule discounts. */
export enum PromotionScope {
  AllProducts = 0,
  Categories = 1,
  Products = 2,
}

export interface PromotionRule {
  id: number;
  name: string;
  description?: string;
  /** Bitmask Mon(bit 0)..Sun(bit 6); 0 means every day. */
  activeDays: number;
  /** "HH:mm:ss" — null means the rule is active all day. */
  timeFrom?: string | null;
  timeTo?: string | null;
  /** UTC instants — null means no date-range restriction. */
  validFrom?: string | null;
  validTo?: string | null;
  discountType: PromotionDiscountType;
  discountValue: number;
  scope: PromotionScope;
  categoryIds: number[];
  productIds: number[];
  minOrderAmount?: number;
  maxDiscountAmount?: number;
  priority: number;
  isActive: boolean;
}

export interface CreatePromotionRuleDTO {
  name: string;
  description?: string;
  activeDays: number;
  timeFrom?: string | null;
  timeTo?: string | null;
  validFrom?: string | null;
  validTo?: string | null;
  discountType: PromotionDiscountType;
  discountValue: number;
  scope: PromotionScope;
  categoryIds: number[];
  productIds: number[];
  minOrderAmount?: number;
  maxDiscountAmount?: number;
  priority: number;
  isActive: boolean;
}

export type UpdatePromotionRuleDTO = CreatePromotionRuleDTO;

/** Mon..Sun in the bit order the backend expects; index is the bit position. */
export const DAY_BITS = [1, 2, 4, 8, 16, 32, 64];
export const ALL_DAYS_MASK = 127;

/**
 * The backend wraps every payload in ApiResponse<T>, so each call unwraps exactly once
 * via `response.data.data`. List endpoints carry their paging metadata as a sibling
 * `pagination` field on the same envelope rather than nested inside `data`.
 */
const promotionService = {
  getAll: async (pageNumber = 1, pageSize = 100) => {
    const response = await apiService.get<PromotionRule[]>('/promotions', { pageNumber, pageSize });
    return {
      items: response.data.data ?? [],
      pagination: response.data.pagination,
    };
  },

  getById: async (id: number) => {
    const response = await apiService.get<PromotionRule>(`/promotions/${id}`);
    return response.data.data;
  },

  create: async (dto: CreatePromotionRuleDTO) => {
    const response = await apiService.post<PromotionRule>('/promotions', dto);
    return response.data.data;
  },

  update: async (id: number, dto: UpdatePromotionRuleDTO) => {
    const response = await apiService.put<PromotionRule>(`/promotions/${id}`, dto);
    return response.data.data;
  },

  delete: (id: number) => apiService.delete(`/promotions/${id}`),
};

export default promotionService;
